import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaShoppingCart,
  FaStar,
  FaArrowLeft,
  FaCheckCircle,
  FaBoxOpen,
  FaTag,
  FaInfoCircle,
  FaListUl,
} from "react-icons/fa";
import { useGetProductByIdQuery } from "../slices/productApiSlice";
import { addToCart } from "../slices/cartSlice";

const UserProductDetailsScreen = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [qty, setQty] = useState(1);
  const { userInfo } = useSelector((state) => state.auth);
  const { data: product, isLoading, error } = useGetProductByIdQuery(productId);

  const addToCartHandler = () => {
    dispatch(addToCart({ ...product, qty }));
    navigate("/cart");
  };

  if (isLoading)
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
      </Container>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error?.data?.message || error.error}
      </Alert>
    );

  return (
    <Container fluid className="p-3 p-md-4">
      {/* Back Button */}
      <Button
        variant="outline-primary"
        className="mb-3 rounded-pill d-flex align-items-center gap-2 shadow-sm"
        onClick={() => navigate("/user-categories")}
      >
        <FaArrowLeft /> Back to Category
      </Button>

      <Row className="g-4">
        {/* Product Image */}
        <Col xs={12} md={6}>
          <Card className="shadow-lg border-0 rounded-4 overflow-hidden h-100">
            <div className="position-relative">
              <Card.Img
                src={product.images?.[0] || "https://via.placeholder.com/400"}
                alt={product.name}
                style={{
                  height: "360px",
                  objectFit: "cover",
                  width: "100%",
                }}
              />
              <Badge
                bg="danger"
                className="position-absolute top-0 start-0 m-2 px-3 py-2 rounded-pill fs-6"
              >
                <FaTag className="me-1" /> Special Offer
              </Badge>
            </div>
          </Card>
        </Col>

        {/* Product Info */}
        <Col xs={12} md={6}>
          <Card className="shadow-sm rounded-4 border-0 bg-light h-100">
            <Card.Body className="p-4">
              <h3 className="fw-bold text-primary mb-2 d-flex align-items-center gap-2">
                <FaBoxOpen /> {product.Tamil} - {product.name}
              </h3>

              <h4 className="text-success fw-semibold mb-3 d-flex align-items-center gap-2">
                ₹ {product.price}
                <Badge bg="warning" text="dark" className="ms-2">
                  <FaStar color="orange" /> {product.rating?.toFixed(1) || 0}
                </Badge>
              </h4>

              <p className="text-secondary d-flex align-items-start gap-2 mb-3">
                <FaInfoCircle size={18} className="mt-1 text-info" />
                {product.description || "No description available."}
              </p>

              <div className="bg-white rounded-4 shadow-sm p-3 mb-3">
                <h6 className="fw-semibold mb-3 text-dark d-flex align-items-center gap-2">
                  <FaListUl className="text-primary" /> Product Details
                </h6>
                <ul className="list-unstyled small text-muted mb-0">
                  <li>
                    <strong>Category:</strong> {product.category || "N/A"}
                  </li>
                  <li>
                    <strong>Subcategory:</strong> {product.subcategory || "N/A"}
                  </li>
                  <li>
                    <strong>In Stock:</strong>{" "}
                    {product.countInStock > 0 ? (
                      <span className="text-success fw-semibold">
                        Yes ({product.countInStock})
                      </span>
                    ) : (
                      <span className="text-danger fw-semibold">Out of Stock</span>
                    )}
                  </li>
                </ul>
              </div>

              <Form.Group as={Row} className="align-items-center mb-4">
                <Form.Label
                  column
                  sm="3"
                  className="fw-semibold text-secondary"
                >
                  Quantity:
                </Form.Label>
                <Col sm="6">
                  <Form.Select
                    value={qty}
                    onChange={(e) => setQty(Number(e.target.value))}
                    className="rounded-pill shadow-sm"
                  >
                    {[...Array(10).keys()].map((x) => (
                      <option key={x + 1} value={x + 1}>
                        {x + 1}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>

              <Button
                onClick={addToCartHandler}
                disabled={product.countInStock === 0}
                variant={
                  product.countInStock === 0 ? "secondary" : "success"
                }
                className="w-100 py-3 rounded-pill shadow d-flex align-items-center justify-content-center gap-2 fw-semibold fs-5"
              >
                {product.countInStock === 0 ? (
                  <>
                    <FaCheckCircle /> Out of Stock
                  </>
                ) : (
                  <>
                    <FaShoppingCart /> Add to Cart
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProductDetailsScreen;
